import { Controller } from '@hotwired/stimulus'
import bootstrap from 'bootstrap/dist/js/bootstrap'

export default class extends Controller {
  static targets = ['form', 'review', 'history']

  // The toggle endpoints return the whole `<tr>`. Swapping the row's cells in
  // place — rather than rebuilding the DataTable — keeps the search term, the
  // sort and the scroll position the admin was working from. DataTables caches
  // the `<td>` nodes it read at init, so the existing cells have to be reused;
  // `row().invalidate()` then re-reads them (including `data-sort`) without a
  // draw, so the toggled row doesn't jump out from under the cursor.
  updateRow(event) {
    const [_data, _status, xhr] = event.detail
    this.replaceRow(event.target.closest('tr'), xhr.response)
  }

  replaceRow(row, html) {
    if (!row) return

    const newRow = this.parseRow(html)
    if (!newRow) return

    this.disposeTooltips(row)
    this.disposeDropdowns(row)

    // Only the cells are synced: the server row carries nothing but its id.
    const cells = row.children
    Array.from(newRow.children).forEach((newCell, index) => {
      if (cells[index]) this.syncCell(cells[index], newCell)
    })

    this.invalidate(row)
    this.initializeTooltips(row)
  }

  parseRow(html) {
    const table = document.createElement('table')
    table.innerHTML = html
    return table.querySelector('tr')
  }

  // DataTables paints its own sorting classes onto the cells; the server has
  // never heard of them, so they are carried across the swap by hand.
  syncCell(cell, newCell) {
    const painted = Array.from(cell.classList).filter((name) =>
      name.startsWith('sorting')
    )

    Array.from(cell.attributes).forEach((attribute) => {
      if (!newCell.hasAttribute(attribute.name)) {
        cell.removeAttribute(attribute.name)
      }
    })
    Array.from(newCell.attributes).forEach((attribute) => {
      cell.setAttribute(attribute.name, attribute.value)
    })
    painted.forEach((name) => cell.classList.add(name))

    cell.innerHTML = newCell.innerHTML
  }

  invalidate(row) {
    const table = row.closest('table')
    if (!table || !$.fn.DataTable.isDataTable(table)) return

    // No `draw()`: the cells already show the new state, and redrawing would
    // re-apply the active sort and filter, throwing the row the admin just
    // clicked somewhere else on the page.
    $(table).DataTable().row(row).invalidate()
  }

  disposeTooltips(root) {
    root.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
      // No hide() first: its fade callback fires after dispose() has nulled
      // the instance and throws. dispose() removes the tip on its own.
      bootstrap.Tooltip.getInstance(element)?.dispose()
    })
  }

  // The cell's innerHTML is replaced wholesale, so an open menu's instance would
  // be left pointing at a detached node.
  disposeDropdowns(root) {
    root.querySelectorAll('[data-bs-toggle="dropdown"]').forEach((element) => {
      bootstrap.Dropdown.getInstance(element)?.dispose()
    })
  }

  initializeTooltips(root) {
    root
      .querySelectorAll('[data-bs-toggle="tooltip"]')
      .forEach((element) => bootstrap.Tooltip.getOrCreateInstance(element))
  }

  updateFields(event) {
    const [data, _status, _xhr] = event.detail

    const restaurantId = data.restaurant_id
    const gbpRow = document.getElementById(`gbp-${restaurantId}`)
    const gbpmRow = document.getElementById(`gbpm-${restaurantId}`)

    if (gbpRow) gbpRow.innerHTML = data.gbp
    if (gbpmRow) gbpmRow.innerHTML = data.gbpm

    const row = (gbpRow || gbpmRow)?.closest('tr')
    if (row) this.invalidate(row)
  }

  // The preview modal's send form is outside the row, so the row it refreshes
  // is named by a Stimulus param instead of found from the event target.
  reviewSent(event) {
    const [_data, _status, xhr] = event.detail
    this.replaceRow(document.getElementById(`row-${event.params.restaurantId}`), xhr.response)
    bootstrap.Modal.getInstance(document.getElementById('reviewModal'))?.hide()

    // The email went; a text that did not is the one thing left to say.
    const notice = xhr.getResponseHeader('X-Review-Notice')
    if (notice) window.alert(notice)
  }

  reviewFailed(event) {
    const [_data, _status, xhr] = event.detail
    const notice = this.reviewTarget.querySelector('[data-review-error]')
    if (!notice) return

    notice.textContent = xhr.responseText || 'The email could not be sent.'
    notice.hidden = false
  }

  // Only the controller's own 4xx bodies are plain text; anything else is
  // an error page and gets the fallback line.
  toggleFailed(event) {
    const [_data, _status, xhr] = event.detail
    const explained = xhr.status >= 400 && xhr.status < 500 && xhr.responseText
    window.alert(explained ? xhr.responseText : 'The setting could not be changed.')
  }

  // Re-render the preview from the whole form — the note and the include
  // checkboxes — so what the admin sees is exactly what the send posts. The URL
  // already carries restaurant_id and index, so the pairs are appended to it.
  // Ticking a box fires this, so the checklist's scroll and the box just
  // clicked are put back on the replacement markup.
  refreshPreview(event) {
    const form = this.reviewTarget.querySelector('form')
    if (!form) return

    const url = new URL(event.params.url, window.location.origin)
    new FormData(form).forEach((value, key) => {
      if (key !== 'authenticity_token') url.searchParams.append(key, value)
    })

    const state = this.checklistState()
    this.togglePreviewLoading(true)
    // Ticking two boxes quickly leaves two requests in flight; only the last
    // one asked for may paint.
    const request = (this.previewRequest = (this.previewRequest || 0) + 1)

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' })
      .then((response) => {
        // A 500, or a re-auth redirect to the login page, would otherwise be
        // painted into the modal as though it were the preview.
        if (!response.ok) throw new Error(response.status)

        return response.text()
      })
      .then((html) => {
        // A later click is still in flight: leave its spinner up.
        if (request !== this.previewRequest) return

        this.reviewTarget.innerHTML = html
        this.restoreChecklist(state)
      })
      .catch(() => {
        if (request === this.previewRequest) this.togglePreviewLoading(false)
      })
  }

  togglePreviewLoading(loading) {
    const loader = this.reviewTarget.querySelector('[data-preview-loading]')
    if (!loader) return

    loader.classList.toggle('d-none', !loading)
    loader.classList.toggle('d-flex', loading)
  }

  checklistState() {
    const focused = document.activeElement
    return {
      scrollTop: this.reviewTarget.querySelector('[data-checklist]')?.scrollTop || 0,
      focusName: focused?.type === 'checkbox' ? focused.name : null
    }
  }

  restoreChecklist({ scrollTop, focusName }) {
    const list = this.reviewTarget.querySelector('[data-checklist]')
    if (list) list.scrollTop = scrollTop
    if (!focusName) return

    this.reviewTarget
      .querySelector(`input[type=checkbox][name="${CSS.escape(focusName)}"]`)
      ?.focus()
  }

  // The note only reaches the preview on the next refresh, so say so while it
  // is being typed.
  markPreviewStale() {
    const notice = this.reviewTarget.querySelector('[data-preview-stale]')
    if (notice) notice.hidden = false
  }

  openReviewModal(event) {
    const [_data, _status, xhr] = event.detail
    this.reviewTarget.innerHTML = xhr.response

    bootstrap.Modal.getOrCreateInstance(document.getElementById('reviewModal')).show()
  }

  openReviewHistoryModal(event) {
    const [_data, _status, xhr] = event.detail
    this.historyTarget.innerHTML = xhr.response
    this.initializeTooltips(this.historyTarget)

    bootstrap.Modal.getOrCreateInstance(document.getElementById('reviewHistoryModal')).show()
  }

  // ajax-form has already filled the calculator's body; the link no longer
  // carries the modal data-api so its data-bs-toggle can hold the tooltip.
  openCostCalculator() {
    bootstrap.Modal.getOrCreateInstance(document.getElementById('costCalculationModal')).show()
  }

  openModal(event) {
    const [_data, _status, xhr] = event.detail
    this.formTarget.innerHTML = xhr.response

    bootstrap.Modal.getOrCreateInstance(document.getElementById('editModal')).show()
  }
}
